import { $ } from './dom.ts';
import { apiFetch, jsonIfOk } from './fetch-helpers.ts';
import type { Revision } from './storage.ts';

/*
 * UI helpers shared by the coding-assignment entry points (index.ts for
 * Monaco-based JS/Java pages and snap.ts for embedded Snap! pages): the
 * revisions dropdown, the teacher student-selector, and the current-repo query
 * param. Kept backend-agnostic so both editors render the same chrome.
 */

export const commitTime = (isodate: string) => {
  const d = new Date(isodate);
  const today = new Date().toDateString();
  const date = d.toDateString();

  const h = d.getHours();

  const hh = ((h + 11) % 12) + 1;
  const mm = `${d.getMinutes()}`.padStart(2, '0');
  const ss = `${d.getSeconds()}`.padStart(2, '0');
  const amPm = h > 12 ? 'pm' : 'am';

  const time = `${hh}:${mm}:${ss} ${amPm}`;

  return date === today ? time : `${date} ${time}`;
};

/*
 * The `student` query param: when a teacher is viewing a specific student's
 * work we must not save into it. (Re-exported from storage.ts, where the
 * ServerStorage constructor also consumes it.)
 */
export { reviewStudent } from './storage.ts';

/*
 * Hide the revisions dropdown on pages whose storage doesn't persist beyond
 * this browser (/m/ previews, demos, anonymous views): there are never
 * any revisions to show, so don't render a permanently empty control.
 */
export const hideRevisionsUnlessSaving = (storage: { saves: boolean }) => {
  const container = $('#revisions-container') as HTMLElement | null;
  if (container && !storage.saves) {
    container.hidden = true;
  }
};

export const populateRevisions = (revs: Revision[]) => {
  const select = $('#file-revisions');
  if (select) {
    select.replaceChildren();
    revs.forEach((r) => {
      const opt = document.createElement('option');
      opt.value = r.sha;
      opt.innerText = commitTime(r.commit.author.date);
      select.append(opt);
    });
  }
};

// "10:30am"-style clock time for the "Last saved" message.
const clockTime = (d: Date) => {
  const h = d.getHours();
  const hh = ((h + 11) % 12) + 1;
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}${h < 12 ? 'am' : 'pm'}`;
};

/*
 * Save feedback shown in the toolbar's `.save-status` element (next to the Run
 * button on Monaco pages, next to Save on Snap! pages). Replaces the element's
 * contents so a spinner and a message can coexist, e.g. a spinner + "Saving…".
 * Used by both index.ts and snap.ts so the two editors report saves the same
 * way. No-ops gracefully if the page has no `.save-status` element.
 */
export const saveStatus = () => {
  const el = $('.editor-toolbar .save-status') as HTMLElement | null;
  const set = (text: string, opts: { spinner?: boolean; error?: boolean } = {}) => {
    if (!el) return;
    el.classList.toggle('error', !!opts.error);
    el.replaceChildren();
    if (opts.spinner) {
      const s = document.createElement('span');
      s.className = 'spinner';
      el.append(s);
    }
    el.append(document.createTextNode(text));
  };
  return {
    saving: () => set('Saving…', { spinner: true }),
    saved: (date: string | number | Date) => set(`Last saved at ${clockTime(new Date(date))}`),
    noChanges: () => set('No changes to save'),
    failed: () => set('Save failed — see console', { error: true }),
    authLost: () =>
      set('Not saved — you are logged out. Log in (in another tab is fine); work is kept here.', {
        error: true,
      }),
    message: (text: string) => set(text),
  };
};

export const addRevision = (commit: { sha: string; author: { date: string } }) => {
  const select = $('#file-revisions');
  if (select) {
    const opt = document.createElement('option');
    opt.value = commit.sha;
    opt.innerText = commitTime(commit.author.date);
    select.prepend(opt);
    opt.selected = true;
  }
};

// The assignment dir from the page URL (`/c/:course/<dir>`) — e.g. 'coding/maze'
// or 'snap/turtles'. Null on non-assignment pages.
const pageDir = (): string | null => {
  const m = window.location.pathname.replace(/^\/+|\/+$/g, '').match(/^c\/[^/]+\/(.+)$/);
  return m?.[1] ?? null;
};

type PickerPairing = {
  members: { username: string; name: string | null; detached: boolean }[];
};

/*
 * Populate the teacher student-selector. On a paired assignment
 * (plans/section-pairs.md) it becomes a PAIR picker: each active pairing is one
 * option ("Ada & Alan") whose value is a following member's username — the
 * server's dirKeyFor resolves that to the pair's shared directory, so picking
 * the pair reviews their shared work. Detached members and unpaired students
 * appear individually. Also drops in a "Live grid" link (coding assignments) to
 * the assignment's grid, where pairs are managed.
 */
export const populateStudents = async (course: string | undefined, selected: string | null) => {
  const select = $('#student-selector') as HTMLSelectElement | null;
  if (!select) return;

  const dir = pageDir();

  const blank = document.createElement('option');
  blank.value = '';
  blank.innerText = '';
  if (!selected) blank.selected = true;
  select.append(blank);

  const students = await apiFetch(`/api/${course}/students`).then(
    jsonIfOk<{ username: string; name: string; class_id: string; period: number }[]>,
  );

  // Periods disambiguate only when the teacher has more than one section:
  // options are grouped into per-period optgroups.
  const multipleSections = new Set(students.map((s) => s.class_id)).size > 1;

  const periodOf = new Map(students.map((s) => [s.username, s.period]));
  const groups = new Map<number, HTMLOptGroupElement>();
  if (multipleSections) {
    for (const p of [...new Set(students.map((s) => s.period))].sort((a, b) => a - b)) {
      const g = document.createElement('optgroup');
      g.label = `Period ${p}`;
      groups.set(p, g);
      select.append(g);
    }
  }
  const groupFor = (username: string): HTMLSelectElement | HTMLOptGroupElement => {
    const p = periodOf.get(username);
    return (p !== undefined ? groups.get(p) : undefined) ?? select;
  };

  // Active pairings for this assignment, if any (a flat list means no pairs yet,
  // or a non-paired assignment — the picker stays a plain student list).
  let pairings: PickerPairing[] = [];
  if (dir) {
    try {
      pairings = (
        await apiFetch(`/api/${course}/pairings/${dir}`).then(
          jsonIfOk<{ pairings: PickerPairing[] }>,
        )
      ).pairings;
    } catch {
      // A pairings hiccup just falls back to the flat list.
    }
  }

  // One option per pair (of following members); those members are then omitted
  // from the individual list. Detached members fall through to it. Member
  // names come from the roster (disambiguated server-side) when it has them.
  const rosterName = new Map(students.map((s) => [s.username, s.name]));
  const covered = new Set<string>();
  for (const p of pairings) {
    const following = p.members.filter((m) => !m.detached);
    if (following.length < 2) continue;
    const opt = document.createElement('option');
    opt.value = following[0].username;
    opt.innerText = following
      .map((m) => rosterName.get(m.username) ?? m.name ?? m.username)
      .join(' & ');
    if (following.some((m) => m.username === selected)) opt.selected = true;
    groupFor(following[0].username).append(opt);
    for (const m of following) covered.add(m.username);
  }

  for (const s of students) {
    if (covered.has(s.username)) continue;
    const opt = document.createElement('option');
    opt.value = s.username;
    opt.innerText = `${s.name} (${s.username})`;
    if (s.username === selected) opt.selected = true;
    groupFor(s.username).append(opt);
  }

  // Live-grid link for coding assignments (where pairs are snapshotted/managed).
  if (dir?.startsWith('coding/') && !document.getElementById('assignment-grid-link')) {
    const link = document.createElement('a');
    link.id = 'assignment-grid-link';
    link.className = 'grid-link';
    link.href = `/c/${course}/coding-grid/${dir.slice('coding/'.length)}`;
    link.textContent = 'Live grid';
    link.title = 'Live grid & pairing for this assignment';
    select.after(link);
  }
};

/*
 * Reload the page viewing the chosen student's work (empty = back to your
 * own). Used as the student-selector's onchange by both editors.
 */
export const switchToStudent = (username: string) => {
  const url = new URL(window.location.href);
  if (username) {
    url.searchParams.set('student', username);
  } else {
    url.searchParams.delete('student');
  }
  window.location.href = url.toString();
};
